
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common'; // Import CommonModule
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu'; // Import MatMenuModule
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip'; // Import MatTooltipModule
import { Router } from '@angular/router';
import { Workspace } from '@app-libs/model';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { LoadingSpinnerComponent } from '../../common/components/loading-spinner';
import { WorkspacesApiService } from '../../services/api/workspaces-api.service';
import { AuthService } from '../../services/auth/auth.service';
import { UserProfileService } from '../../services/auth/user-profile.service';
import { EventBusService } from '../../services/client/event-bus.service';
import { ChatCardComponent } from '../common/chat-card/chat-card.component';
import { DataTableComponent } from '../common/data-table/data-table.component';
import { SearchBarComponent } from '../common/search-bar/search-bar.component';

@Component({
  selector: 'app-workspaces',
  imports: [
    MatCardModule,
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatMenuModule,
    ReactiveFormsModule,
    SearchBarComponent,
    DataTableComponent,
    ChatCardComponent,
    LoadingSpinnerComponent
  ],
  templateUrl: './workspaces.component.html',
  styleUrls: ['./workspaces.component.scss'],
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('600ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class WorkspacesComponent implements OnDestroy {
  workspaces: Workspace[] = [];
  filteredWorkspaces: Workspace[] = [];
  loading = true;
  isLoading$ = new BehaviorSubject<boolean>(true); // Use BehaviorSubject for isLoading
  selectedWorkspace = "";

  // displayedColumns: string[] = ['name', 'description', 'category', 'action'];
  displayedColumns: string[] = ['description', 'action'];
  searchControl = new FormControl('');
  searchPlaceholder = 'Find your workspace';
  listHeadingPlaceholder = 'Workspaces';
  searchTooltip = 'Search for workspaces by name or description';
  private destroy$ = new Subject<void>();

  // For data table
  columns = [
    {
      key: 'description',
      header: 'Select your workspace',
      isDetails: true,
      fields: ['name', 'description'],
      classes: ['name-col', 'description-col'],
    },
    {
      key: 'action',
      header: '',
      isAction: true,
      actionIcon: 'radio_button_unchecked',
      activeActionIcon: 'radio_button_checked',
      tooltip: 'Activate workspace',
    },
  ];

  constructor(
    private router: Router,
    private workspacesService: WorkspacesApiService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private profileService: UserProfileService,
    private authService: AuthService,
    private eventBus: EventBusService
  ) {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value) =>
        this.filterWorkspaces(value || '')
      );

    // Get selected workspace
    this.profileService.getUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        // console.log('User profile:', profile);
        if (profile?.selected_workspace) {
          console.log('Selected workspace is:', profile.selected_workspace);
          this.selectedWorkspace = profile.selected_workspace;
        }
      });

    this.initializeWorkspaces();
  }

  async initializeWorkspaces() {

    this.workspaces = await this.workspacesService.getWorkspacesForCurrentUser().toPromise() || [];
    // this.workspaces = [];
    this.filteredWorkspaces = this.workspaces;
    this.loading = false;
    // Emit false to indicate loading is complete
    this.isLoading$.next(false);
    this.cdr.detectChanges(); // Ensure the view is updated
  }

  filterWorkspaces(query: string) {
    console.log('Filtering workspaces with query:', query);
    let regex: RegExp;
    try {
      regex = new RegExp(query, 'i');
    } catch (e) {
      console.error('Invalid regex:', e);
      this.snackBar.open('Invalid regex pattern', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.filteredWorkspaces = this.workspaces?.filter(
      (workspace) =>
        regex.test(workspace.name || '') ||
        regex.test(workspace.description || '') ||
        workspace.name?.toLowerCase().includes(query.toLowerCase()) ||
        workspace.description?.toLowerCase().includes(query.toLowerCase())
    );
    console.log('Filtered workspaces:', this.filteredWorkspaces);
  }

  async activateWorkspace(event: any, workspace: Workspace) {
    console.log(`activateWorkspace ${workspace.name}`);

    const userId = this.authService.getCurrentUserId() || '';
    // const updated = await this.profileService.updateSelectedWorkspace(userId, workspace.workspace_id);
    this.profileService.updateSelectedWorkspace(userId, workspace.workspace_id).then((profile) => {
      console.log('Updated selected workspace for user:', profile?.email);
      this.selectedWorkspace = workspace.workspace_id;

      // Notify app that the workspace has been updated - after the backend update is complete
      this.eventBus.emitWorkspaceUpdated({
        workspaceId: workspace.workspace_id,
        workspaceName: workspace.name
      });

      // Navigate after successful update
      this.router.navigate(['/assistants']);
    });

    event.preventDefault();
    event.stopPropagation();
  }

  onRowClick(event: any) {
    // console.log('Row clicked:', event);
    this.activateWorkspace(event.event, event.row);
  }

  onActionClick(event: any) {
    // console.log('Action clicked:', event);
    this.activateWorkspace(event.event, event.row);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
